import { CAST_AI } from '@cast/constants';
import {
  tokens,
  TreeMapTooltip,
  type TreeMapTooltipRow,
  Typography,
} from '@cast/design-system';
import type { SloMetric } from '@cast/features-cluster-shared-utils';
import { ClusterDisplayStatus } from '@cast/types';

import {
  buildReliabilityTooltipRows,
  shouldShowAgentInstallCta,
  type ReliabilityTooltipRow,
} from '../utils/reliabilityHeatmapTooltip';

const toneToColor = (
  tone: ReliabilityTooltipRow['tone']
): string | undefined => {
  switch (tone) {
    case 'error':
      return tokens.theme.fg.status.error;
    case 'warning':
      return tokens.theme.fg.status.warning;
    case 'success':
      return tokens.theme.fg.status.success;
    case 'secondary':
      return tokens.theme.fg.structural.secondary;
    default:
      return undefined;
  }
};

const toTreeMapRows = (rows: ReliabilityTooltipRow[]): TreeMapTooltipRow[] =>
  rows.map((row) => ({
    label: row.label,
    value: row.value,
    valueColor: toneToColor(row.tone),
  }));

type ClusterReliabilityHeatmapTooltipProps = {
  clusterName: string;
  displayStatus?: ClusterDisplayStatus;
  displayStatusLabel?: string;
  metrics: SloMetric[];
};

/**
 * Passive reliability tooltip for heatmap cells.
 * Install guidance is informational — opening the cluster (cell click / Enter)
 * is the action, matching chart tooltip patterns (no interactive overlay).
 */
export const ClusterReliabilityHeatmapTooltip = ({
  clusterName,
  displayStatus,
  displayStatusLabel,
  metrics,
}: ClusterReliabilityHeatmapTooltipProps) => {
  const rows = toTreeMapRows(buildReliabilityTooltipRows(metrics));
  const showAgentCta = shouldShowAgentInstallCta(displayStatus);

  const footer = showAgentCta ? (
    <Typography
      variant="P10R"
      color={tokens.theme.fg.structural.secondary}
      sx={{ width: 227, maxWidth: '100%', display: 'block' }}
    >
      <Typography
        component="span"
        variant="P10R"
        color={tokens.theme.fg.action.primary}
      >
        Install {CAST_AI} agent
      </Typography>
      {
        ' to track more reliability metrics like: Latency, Error rate, Request rate, OOMKills, Restarts, Disruption.'
      }
    </Typography>
  ) : undefined;

  return (
    <TreeMapTooltip
      title={clusterName}
      status={displayStatusLabel}
      statusColor={tokens.theme.fg.structural.secondary}
      rows={rows}
      footer={footer}
      testId="cluster-reliability-heatmap-tooltip"
    />
  );
};
